/*
 * Copyright (C) 2018 - present by Potentially
 *
 * Please see distribution for license.
 */

import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { environment } from '@app/environments/environment';
import { TranslocoService } from '@ngneat/transloco';
import { catchError, switchMap } from 'rxjs/operators';
import { RestClientService } from '../../../shared/services/rest-client.service';
import { ObservableResult } from '../../../shared/store';
import {
  GoogleCalendarAuthUrlResponse,
  GoogleCalendarBulkSyncResponse,
  GoogleCalendarDataService,
  GoogleCalendarDisconnectResponse,
  GoogleCalendarStatusResponse,
  GoogleCalendarSyncEventResponse,
} from './google-calendar-data.service';

@Injectable()
export class ApiGoogleCalendarDataService implements GoogleCalendarDataService {
  private baseUrl = Location.joinWithSlash(environment.apiRootUrl || '', 'google');

  constructor(
    private restClient: RestClientService,
    private translocoService: TranslocoService,
  ) {}

  getAuthUrl(): ObservableResult<GoogleCalendarAuthUrlResponse> {
    return this.restClient.get<GoogleCalendarAuthUrlResponse>(`${this.baseUrl}/auth-url`).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGoogleCalendarGetAuthUrl'))),
    );
  }

  getStatus(): ObservableResult<GoogleCalendarStatusResponse> {
    return this.restClient.get<GoogleCalendarStatusResponse>(`${this.baseUrl}/calendars/status`).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGoogleCalendarGetStatus'))),
    );
  }

  disconnect(): ObservableResult<GoogleCalendarDisconnectResponse> {
    return this.restClient.delete<GoogleCalendarDisconnectResponse>(`${this.baseUrl}/calendars/disconnect`).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGoogleCalendarDisconnect'))),
    );
  }

  syncAll(): ObservableResult<GoogleCalendarBulkSyncResponse> {
    return this.restClient.post<GoogleCalendarBulkSyncResponse>(`${this.baseUrl}/calendars/sync-all`, {}).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGoogleCalendarSyncEvents'))),
    );
  }

  syncEvent(eventId: string): ObservableResult<GoogleCalendarSyncEventResponse> {
    return this.restClient.post<GoogleCalendarSyncEventResponse>(`${this.baseUrl}/calendars/sync-event/${eventId}`, {}).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGoogleCalendarSyncEvent'))),
    );
  }
}
